/**
 * Today, triggers are created to trigger/run jobs.
 * So, this panel is displayed while creating a trigger if the project
 * or entity (in the case of triggers from the Model registry)
 * has no jobs in it.
 */
import {TargetBlank} from '@wandb/weave/common/util/links';
// @todo: Migrate to @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
// @todo: Migrate to 'SkipToContent' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Content, List, Root, Trigger} from '@wandb/weave/components/Tabs';
import React, {useState} from 'react';
import {useShallow} from 'zustand/react/shallow';

import {useTeamMember} from '../../../../state/graphql/teamMembers';
import {useViewer} from '../../../../state/viewer/hooks';
import DocUrls from '../../../../util/doc_urls';
import * as urls from '../../../../util/urls';
import {useActionConfigStore} from './ActionStore';
import * as S from './EmptyTriggerDrawer.styles';

type EmptyTriggerDrawerProps = {};

export const EmptyTriggerDrawer = () => {
  const [tab, setTab] = useState('jobs');
  const viewer = useViewer();
  const actionConfig = useActionConfigStore(useShallow(s => s));
  const {viewerIsTeamAdmin} = useTeamMember({
    viewer,
    entityName: actionConfig.entityName,
  });
  return (
    <S.Panel>
      <S.PanelInner>
        <S.Circle>
          <S.LightBulb />
        </S.Circle>
        <S.Heading>No jobs or webhooks found!</S.Heading>
        <S.Message>
          This entity does not have any jobs or webhooks to power your{' '}
          <TargetBlank href={DocUrls.automation}>
            <S.BoldText isLink>automations</S.BoldText>
          </TargetBlank>
        </S.Message>
        <S.Body>
          <S.BodyHeader>What are automations?</S.BodyHeader>
          <S.BodyText>
            Automations allow you to link an event in the model registry or
            artifacts to a downstream action that should kick off when the event
            is performed. Create an automation where linking a new model version
            kicks off an evaluation job, or adding the alias “production” kicks
            off a POST request to an external system to containerize and deploy
            the model.
            <S.Spacer />
            An automation can trigger 2 kinds of actions:{' '}
            <S.BoldText>Jobs</S.BoldText> or <S.BoldText>Webhooks</S.BoldText>.
          </S.BodyText>
          <Root value={tab} onValueChange={d => setTab(d)}>
            <List style={{margin: '0 40px'}}>
              <Trigger value="jobs">Using jobs</Trigger>
              <Trigger value="webhooks">Using webhooks</Trigger>
            </List>
            <Content value="jobs">
              <S.BodyText>
                <S.BoldText>Jobs</S.BoldText> are reusable, configurable run
                templates that allow you to quickly launch new runs or create
                triggers, locally or using extra GPU.
              </S.BodyText>
              <S.Buttons>
                <TargetBlank
                  href={DocUrls.automation + '#create-a-launch-automation'}>
                  <Button
                    size="large"
                    variant="secondary"
                    icon="documentation"
                    style={{marginRight: '8px'}}>
                    Jobs documentation
                  </Button>
                </TargetBlank>
              </S.Buttons>
            </Content>
            <Content value="webhooks">
              <S.BodyText>
                Configure <S.BoldText>webhooks</S.BoldText> to establish
                seamless connections between different applications. Leverage
                the power of webhooks to fuel automations and alerts within your
                team's scope.
                <S.Spacer />
                {viewerIsTeamAdmin ? (
                  <>
                    To create a webhook, go to the{' '}
                    <TargetBlank
                      href={urls.teamSettings(actionConfig.entityName!)}>
                      <S.BoldText isLink>Team settings</S.BoldText>
                    </TargetBlank>{' '}
                    section.
                  </>
                ) : (
                  <>To create a webhook, contact your team administrator.</>
                )}
              </S.BodyText>
              <S.Buttons>
                <TargetBlank
                  href={DocUrls.automation + '#create-a-webhook-automation'}>
                  <Button
                    size="large"
                    variant="secondary"
                    icon="documentation"
                    style={{marginRight: '8px'}}>
                    Webhook documentation
                  </Button>
                </TargetBlank>
              </S.Buttons>
            </Content>
          </Root>
        </S.Body>
      </S.PanelInner>
    </S.Panel>
  );
};
